import { Breadcrumb, Button, Card, Label, Modal, Table, TextInput } from "flowbite-react";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { HiHome, HiOutlinePencilAlt, HiPlus, HiTrash, HiUserGroup } from "react-icons/hi";
import NavbarSidebarLayout from "../layouts/navbar-sidebar";
import toast from "react-hot-toast";
import { FadeLoader } from "react-spinners";
import { userService } from "../services/main";

const UsersPage: FC = function () {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const currentUser = JSON.parse(sessionStorage.getItem("user") || "{}");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers();
      setUsers(response.data.users || []);
    } catch (error: any) {
      console.error("Failed to fetch users", error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to fetch users";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.firstName?.toLowerCase().includes(searchLower) ||
      user.lastName?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.mobileNumber?.toLowerCase().includes(searchLower)
    );
  });

  // Check if current user is admin
  const isAdmin = currentUser?.role === "ADMIN";

  if (!isAdmin) {
    return (
      <NavbarSidebarLayout>
        <div className="px-4 pt-6 pb-8">
          <Card className="border-0 shadow-lg">
            <div className="text-center py-12">
              <HiUserGroup className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Access Denied
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                Only administrators can access user management.
              </p>
            </div>
          </Card>
        </div>
      </NavbarSidebarLayout>
    );
  }

  return (
    <NavbarSidebarLayout>
      <div className="px-4 pt-6 pb-8">
        {/* Header Section */}
        <div className="mb-6">
          <Breadcrumb className="mb-4">
            <Breadcrumb.Item href="/">
              <div className="flex items-center gap-x-3 cursor-pointer">
                <HiHome className="text-xl" />
                <span className="dark:text-white">Home</span>
              </div>
            </Breadcrumb.Item>
            <Breadcrumb.Item>User Management</Breadcrumb.Item>
          </Breadcrumb>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <HiUserGroup className="text-blue-600 dark:text-blue-400" />
                User Management
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Manage platform users and their access
              </p>
            </div>
          </div>
        </div>

        {/* Search and Actions */}
        <Card className="mb-6 border-0 shadow-lg backdrop-blur-sm">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex-1 w-full sm:w-auto">
              <div className="relative">
                <TextInput
                  type="text"
                  placeholder="Search users by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
            <AddUserModal onUpdate={fetchUsers} />
          </div>
        </Card>

        {/* Table Section */}
        <Card className="border-0 shadow-lg">
          <AllUsersTable
            users={filteredUsers}
            loading={loading}
            onUpdate={fetchUsers}
            currentUserId={currentUser?.id}
          />
        </Card>
      </div>
    </NavbarSidebarLayout>
  );
};

// Add User Modal
const AddUserModal: FC<{ onUpdate: () => void }> = ({ onUpdate }) => {
  const [isOpen, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    password: "",
    role: "USER_MANAGER",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.mobileNumber || !formData.password) {
      toast.error("All fields are required");
      return;
    }

    try {
      setLoading(true);
      await userService.createUser(formData, true);
      toast.success("User created successfully!");
      setOpen(false);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        mobileNumber: "",
        password: "",
        role: "USER_MANAGER",
      });
      onUpdate();
    } catch (error: any) {
      console.error("Failed to create user", error);
      toast.error(error.response?.data?.message || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button color="primary" onClick={() => setOpen(true)}>
        <HiPlus className="mr-2 text-lg" />
        Add User
      </Button>

      <Modal size="xl" onClose={() => setOpen(false)} show={isOpen} dismissible>
        <Modal.Header className="border-b border-gray-200 !p-4 dark:border-gray-700">
          <strong>Add New User</strong>
        </Modal.Header>
        <Modal.Body className="max-h-[70vh] overflow-y-auto">
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex flex-col">
                <Label htmlFor="firstName" className="mb-1 text-sm">
                  First Name <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  sizing="sm"
                  required
                />
              </div>
              <div className="flex flex-col">
                <Label htmlFor="lastName" className="mb-1 text-sm">
                  Last Name <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  sizing="sm"
                  required
                />
              </div>
              <div className="flex flex-col">
                <Label htmlFor="email" className="mb-1 text-sm">
                  Email <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  sizing="sm"
                  required
                />
              </div>
              <div className="flex flex-col">
                <Label htmlFor="mobileNumber" className="mb-1 text-sm">
                  Mobile Number <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  id="mobileNumber"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  sizing="sm"
                  required
                />
              </div>
              <div className="flex flex-col">
                <Label htmlFor="password" className="mb-1 text-sm">
                  Password <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  sizing="sm"
                  required
                />
              </div>
              <div className="flex flex-col">
                <Label htmlFor="role" className="mb-1 text-sm">
                  Role
                </Label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="USER_MANAGER">User Manager</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-t border-gray-200 dark:border-gray-700 !p-4">
          <div className="flex justify-end gap-2 w-full">
            <Button color="gray" onClick={() => setOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleSubmit} disabled={loading}>
              {loading ? "Creating..." : "Create User"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// Edit User Modal
const EditUserModal: FC<{ user: any; onUpdate: () => void }> = ({ user, onUpdate }) => {
  const [isOpen, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    email: user.email || "",
    mobileNumber: user.mobileNumber || "",
    role: user.role || "USER_MANAGER",
  });

  useEffect(() => {
    setFormData({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      mobileNumber: user.mobileNumber || "",
      role: user.role || "USER_MANAGER",
    });
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      await userService.updateUser(user.id.toString(), formData);
      toast.success("User updated successfully!");
      setOpen(false);
      onUpdate();
    } catch (error: any) {
      console.error("Failed to update user", error);
      toast.error(error.response?.data?.message || "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button color="gray" size="sm" onClick={() => setOpen(true)}>
        <HiOutlinePencilAlt className="text-lg" />
      </Button>

      <Modal size="xl" onClose={() => setOpen(false)} show={isOpen} dismissible>
        <Modal.Header className="border-b border-gray-200 !p-4 dark:border-gray-700">
          <strong>Edit User</strong>
        </Modal.Header>
        <Modal.Body className="max-h-[70vh] overflow-y-auto">
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex flex-col">
                <Label htmlFor="edit-firstName" className="mb-1 text-sm">
                  First Name
                </Label>
                <TextInput
                  id="edit-firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  sizing="sm"
                />
              </div>
              <div className="flex flex-col">
                <Label htmlFor="edit-lastName" className="mb-1 text-sm">
                  Last Name
                </Label>
                <TextInput
                  id="edit-lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  sizing="sm"
                />
              </div>
              <div className="flex flex-col">
                <Label htmlFor="edit-email" className="mb-1 text-sm">
                  Email
                </Label>
                <TextInput
                  id="edit-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  sizing="sm"
                  disabled
                />
              </div>
              <div className="flex flex-col">
                <Label htmlFor="edit-mobileNumber" className="mb-1 text-sm">
                  Mobile Number
                </Label>
                <TextInput
                  id="edit-mobileNumber"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  sizing="sm"
                />
              </div>
              <div className="flex flex-col">
                <Label htmlFor="edit-role" className="mb-1 text-sm">
                  Role
                </Label>
                <select
                  id="edit-role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="USER_MANAGER">User Manager</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-t border-gray-200 dark:border-gray-700 !p-4">
          <div className="flex justify-end gap-2 w-full">
            <Button color="gray" onClick={() => setOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleSubmit} disabled={loading}>
              {loading ? "Updating..." : "Update User"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// Delete User Modal
const DeleteUserModal: FC<{ user: any; onUpdate: () => void; currentUserId?: number }> = ({
  user,
  onUpdate,
  currentUserId,
}) => {
  const [isOpen, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    try {
      setLoading(true);
      await userService.deleteUser(user.id.toString());
      toast.success("User deleted successfully!");
      setOpen(false);
      onUpdate();
    } catch (error: any) {
      console.error("Failed to delete user", error);
      toast.error(error.response?.data?.message || "Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  const isCurrentUser = currentUserId === user.id;

  return (
    <>
      <Button
        color="failure"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={isCurrentUser}
        title={isCurrentUser ? "Cannot delete your own account" : "Delete user"}
      >
        <HiTrash className="text-lg" />
      </Button>

      <Modal onClose={() => setOpen(false)} show={isOpen} size="md">
        <Modal.Header className="border-b border-gray-200 !p-4 dark:border-gray-700">
          <strong>Delete User</strong>
        </Modal.Header>
        <Modal.Body className="!p-4">
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Are you sure you want to delete user <strong>{user.firstName} {user.lastName}</strong> ({user.email})?
            </p>
            <p className="text-xs text-red-600 dark:text-red-400">
              This action cannot be undone.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-t border-gray-200 dark:border-gray-700 !p-4">
          <div className="flex justify-end gap-2 w-full">
            <Button color="gray" onClick={() => setOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button color="failure" onClick={handleDelete} disabled={loading}>
              {loading ? "Deleting..." : "Delete User"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// All Users Table
const AllUsersTable: FC<{ users: any[]; loading: boolean; onUpdate: () => void; currentUserId?: number }> = ({
  users,
  loading,
  onUpdate,
  currentUserId,
}) => {
  const getRoleBadge = (role: string) => {
    return role === "ADMIN" ? (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
        <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
        Admin
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
        User Manager
      </span>
    );
  };

  return (
    <div className="overflow-x-auto">
      <Table className="min-w-full">
        <Table.Head className="bg-gray-50 dark:bg-gray-800 border-b-2 border-gray-200 dark:border-gray-700">
          <Table.HeadCell className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">
            User
          </Table.HeadCell>
          <Table.HeadCell className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">
            Email
          </Table.HeadCell>
          <Table.HeadCell className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">
            Mobile
          </Table.HeadCell>
          <Table.HeadCell className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">
            Role
          </Table.HeadCell>
          <Table.HeadCell className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">
            Created At
          </Table.HeadCell>
          <Table.HeadCell className="px-4 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">
            Actions
          </Table.HeadCell>
        </Table.Head>
        <Table.Body className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {loading ? (
            <Table.Row>
              <Table.Cell colSpan={6} className="text-center py-12">
                <div className="flex flex-col justify-center items-center">
                  <FadeLoader color="#3b82f6" />
                  <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading users...</p>
                </div>
              </Table.Cell>
            </Table.Row>
          ) : users.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={6} className="text-center py-12">
                <div className="flex flex-col items-center justify-center">
                  <HiUserGroup className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-3" />
                  <p className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                    No users found
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Create your first user to get started
                  </p>
                </div>
              </Table.Cell>
            </Table.Row>
          ) : (
            users.map((user) => (
              <Table.Row
                key={user.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <Table.Cell className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-indigo-500 flex items-center justify-center text-white font-semibold text-sm">
                      {user.firstName?.[0]?.toUpperCase() || ""}{user.lastName?.[0]?.toUpperCase() || ""}
                    </div>
                    <div>
                      <div className="font-medium text-sm text-gray-900 dark:text-white">
                        {user.firstName} {user.lastName}
                      </div>
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell className="px-4 py-3 whitespace-nowrap">
                  <span className="text-sm text-gray-900 dark:text-white">{user.email}</span>
                </Table.Cell>
                <Table.Cell className="px-4 py-3 whitespace-nowrap">
                  <span className="text-sm text-gray-900 dark:text-white">{user.mobileNumber || "-"}</span>
                </Table.Cell>
                <Table.Cell className="px-4 py-3 whitespace-nowrap">
                  {getRoleBadge(user.role)}
                </Table.Cell>
                <Table.Cell className="px-4 py-3 whitespace-nowrap">
                  <span className="text-sm text-gray-900 dark:text-white">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </span>
                </Table.Cell>
                <Table.Cell className="px-4 py-3 whitespace-nowrap text-center">
                  <div className="flex items-center justify-center gap-2">
                    <EditUserModal user={user} onUpdate={onUpdate} />
                    <DeleteUserModal user={user} onUpdate={onUpdate} currentUserId={currentUserId} />
                  </div>
                </Table.Cell>
              </Table.Row>
            ))
          )}
        </Table.Body>
      </Table>
    </div>
  );
};

export default UsersPage;

